import React, { useEffect, useMemo, useRef, useState } from "react";
import { Country } from "country-state-city";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { useLocation, useNavigate } from "react-router-dom";

import { Footer } from "components/Footer";
import Header from "components/Header";
import { useToast } from "components/toast/ToastProvider";
import { env } from "config/env";
import { PATHS } from "routes/paths";
import { authService } from "services/auth/authService";
import { subscriptionService } from "services/subscription/subscriptionService";
import { userService } from "services/user/userService";
import { AuthUser } from "types/user";
import { getFullName } from "utils/auth";
import { getErrorMessage } from "utils/http";
import { authStorage } from "utils/storage";
import { isValidEmail, isValidPassword } from "utils/validation";

type CheckoutPlan = {
  id?: string | number;
  planId?: number;
  title?: string;
  name?: string;
  price?: number;
  priceLabel?: string;
};

type CheckoutLocationState = {
  plan?: CheckoutPlan;
};

type CheckoutForm = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneCountry: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  iban: string;
};

type CheckoutErrors = Partial<Record<keyof CheckoutForm, string>>;

const DEFAULT_PLAN: Required<CheckoutPlan> = {
  id: 3,
  planId: 3,
  title: "Standard",
  name: "Standard Monthly",
  price: 399,
  priceLabel: "59",
};

const PAYMENT_REDIRECT_SECONDS = 10;
const FALLBACK_COUNTRY_CODE = "CY";

type IpinfoLiteResponse = {
  country_code?: string;
};

const splitName = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
};

const COUNTRY_SEARCH_ALIASES: Record<string, string[]> = {
  GB: ["england", "scotland", "wales", "northern ireland", "uk", "britain"],
};

const formatPhoneCode = (phonecode = "") => {
  return phonecode ? `+${phonecode.replace(/^\+/, "")}` : "";
};

const getFlagImageUrl = (isoCode = "") => {
  return isoCode ? `https://flagcdn.com/w20/${isoCode.toLowerCase()}.png` : "";
};

const getPhoneCountryCode = (isoCode = "") => {
  return /^[A-Z]{2}$/.test(isoCode) ? (isoCode as CountryCode) : undefined;
};

const extractVerificationId = (url: string | null) => {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.searchParams.get("verification_id") ?? parsedUrl.searchParams.get("verificationId") ?? undefined;
  } catch {
    return undefined;
  }
};

const getEmailError = (email: string) => {
  const trimmedEmail = email.trim();

  if (!trimmedEmail) return "Email is required";
  if (!isValidEmail(trimmedEmail)) return "Enter a valid email";

  return "";
};

const getPhoneError = (phone: string, countryCode = "", countryName = "selected country") => {
  const trimmedPhone = phone.trim();

  if (!trimmedPhone) return "Phone number is required";
  if (/[^+0-9\s\-()]/.test(trimmedPhone)) return "Phone can only contain numbers";

  const parsedPhone = parsePhoneNumberFromString(trimmedPhone, getPhoneCountryCode(countryCode));

  if (!parsedPhone?.isValid()) return `Enter a valid phone number for ${countryName}`;

  return "";
};

const formatPhoneForSubmit = (phone: string, countryCode = "", fallbackPhoneCode = "") => {
  const trimmedPhone = phone.trim();
  const parsedPhone = parsePhoneNumberFromString(trimmedPhone, getPhoneCountryCode(countryCode));

  if (parsedPhone?.isValid()) return parsedPhone.formatInternational();

  return `${fallbackPhoneCode} ${trimmedPhone}`.trim();
};

export const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useToast();
  const selectedPlan = ((location.state as CheckoutLocationState | null)?.plan ?? DEFAULT_PLAN) as CheckoutPlan;
  const normalizedPlan = {
    ...DEFAULT_PLAN,
    ...selectedPlan,
  };
  const planDisplayName = normalizedPlan.title || normalizedPlan.name;
  const planApiName = normalizedPlan.name || `${planDisplayName} Monthly`;
  const planId = Number(normalizedPlan.planId ?? normalizedPlan.id);
  const planAmount = Number(normalizedPlan.price ?? normalizedPlan.priceLabel);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const [isLoggedIn, setIsLoggedIn] = useState(authStorage.hasToken());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentNotice, setShowPaymentNotice] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState(PAYMENT_REDIRECT_SECONDS);
  const [showPassword, setShowPassword] = useState(false);
  const [showPhoneCountryMenu, setShowPhoneCountryMenu] = useState(false);
  const [phoneCountrySearch, setPhoneCountrySearch] = useState("");
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const [errors, setErrors] = useState<CheckoutErrors>({});
  const phoneCountryRef = useRef<HTMLDivElement>(null);
  const phoneCountrySearchRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<CheckoutForm>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phoneCountry: FALLBACK_COUNTRY_CODE,
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: FALLBACK_COUNTRY_CODE,
    iban: "",
  });

  const selectedCountry = countries.find((country) => country.isoCode === formData.country);
  const selectedPhoneCountry = countries.find((country) => country.isoCode === formData.phoneCountry) ?? selectedCountry;
  const filteredPhoneCountries = useMemo(() => {
    const searchText = phoneCountrySearch.trim().toLowerCase();

    if (!searchText) return countries;

    return countries.filter((country) => {
      const searchableText = [
        country.name,
        country.isoCode,
        formatPhoneCode(country.phonecode),
        ...(COUNTRY_SEARCH_ALIASES[country.isoCode] ?? []),
      ].join(" ").toLowerCase();

      return searchableText.includes(searchText);
    });
  }, [countries, phoneCountrySearch]);
  const phoneCode = selectedPhoneCountry ? formatPhoneCode(selectedPhoneCountry.phonecode) : "";
  const phoneCountryLabel = phoneCode;
  const submitPhone = formatPhoneForSubmit(formData.phone, selectedPhoneCountry?.isoCode, phoneCode);
  const formattedAmount = Number.isFinite(planAmount) ? `EUR ${planAmount.toFixed(2)}` : "EUR 0.00";
  const nextBillingDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    const resolveCountryCode = (countryCode = "") => {
      const normalizedCountryCode = countryCode.trim().toUpperCase();

      if (countries.some((country) => country.isoCode === normalizedCountryCode)) {
        return normalizedCountryCode;
      }

      console.warn("[checkout:geo] Unknown IPInfo country_code, falling back to Cyprus.", {
        countryCode,
        fallbackCountryCode: FALLBACK_COUNTRY_CODE,
      });

      return FALLBACK_COUNTRY_CODE;
    };

    const controller = new AbortController();

    const detectCountry = async () => {
      try {
        console.info("[checkout:geo] Starting IPInfo Lite country detection.");

        if (!env.ipinfoLiteToken) {
          console.warn(
            "[checkout:geo] Missing REACT_APP_IPINFO_LITE_TOKEN in the running frontend bundle. If .env.local was just updated, stop and restart npm start."
          );
          throw new Error("Missing IPInfo Lite token.");
        }

        const response = await fetch(
          `https://api.ipinfo.io/lite/me?token=${encodeURIComponent(env.ipinfoLiteToken)}`,
          { signal: controller.signal }
        );

        console.info("[checkout:geo] IPInfo Lite response received.", {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
        });

        if (!response.ok) {
          throw new Error("Unable to detect country.");
        }

        const data = (await response.json()) as IpinfoLiteResponse;
        console.info("[checkout:geo] IPInfo Lite country_code received.", {
          countryCode: data.country_code,
        });

        const countryCode = resolveCountryCode(data.country_code);

        setFormData((current) => ({
          ...current,
          country: countryCode,
          phoneCountry: countryCode,
        }));

        console.info("[checkout:geo] Checkout country controls updated.", {
          countryCode,
          usedFallback: countryCode === FALLBACK_COUNTRY_CODE && data.country_code?.toUpperCase() !== FALLBACK_COUNTRY_CODE,
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;

        console.warn("[checkout:geo] IPInfo Lite detection failed, falling back to Cyprus.", {
          error,
          fallbackCountryCode: FALLBACK_COUNTRY_CODE,
        });

        setFormData((current) => ({
          ...current,
          country: FALLBACK_COUNTRY_CODE,
          phoneCountry: FALLBACK_COUNTRY_CODE,
        }));
      } finally {
        if (!controller.signal.aborted) {
          setIsDetectingCountry(false);
        }
      }
    };

    void detectCountry();

    return () => controller.abort();
  }, [countries]);

  useEffect(() => {
    const storedUser = authStorage.getUser<AuthUser>();

    if (storedUser) {
      const name = splitName(storedUser.name);
      setFormData((current) => ({
        ...current,
        email: storedUser.email || current.email,
        firstName: name.firstName || current.firstName,
        lastName: name.lastName || current.lastName,
      }));
    }

    if (!authStorage.hasToken()) return;

    const loadProfile = async () => {
      try {
        const response = await userService.getDashboard();
        setFormData((current) => ({
          ...current,
          email: response.data.email || current.email,
          firstName: response.data.firstname || current.firstName,
          lastName: response.data.lastname || current.lastName,
        }));
      } catch {
        // Stored auth details are enough to keep checkout usable.
      }
    };

    void loadProfile();
  }, []);

  useEffect(() => {
    if (!showPaymentNotice) return undefined;

    setRedirectSeconds(PAYMENT_REDIRECT_SECONDS);

    const intervalId = window.setInterval(() => {
      setRedirectSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          navigate(PATHS.account, { replace: true });
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [navigate, showPaymentNotice]);

  useEffect(() => {
    if (!showPhoneCountryMenu) return undefined;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!phoneCountryRef.current?.contains(event.target as Node)) {
        setShowPhoneCountryMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showPhoneCountryMenu]);

  useEffect(() => {
    if (showPhoneCountryMenu) {
      phoneCountrySearchRef.current?.focus();
    }
  }, [showPhoneCountryMenu]);

  const handlePhoneCountrySelect = (isoCode: string) => {
    const nextPhoneCountry = countries.find((country) => country.isoCode === isoCode);

    setFormData((current) => ({
      ...current,
      phoneCountry: isoCode,
    }));

    setErrors((current) => ({
      ...current,
      phone: formData.phone.trim()
        ? getPhoneError(formData.phone, nextPhoneCountry?.isoCode, nextPhoneCountry?.name)
        : current.phone,
    }));

    setShowPhoneCountryMenu(false);
    setPhoneCountrySearch("");
  };

  const validate = () => {
    const nextErrors: CheckoutErrors = {};
    const emailError = getEmailError(formData.email);
    const phoneError = getPhoneError(formData.phone, selectedPhoneCountry?.isoCode, selectedPhoneCountry?.name);

    if (emailError) nextErrors.email = emailError;

    if (!isLoggedIn) {
      if (!formData.password) nextErrors.password = "Password is required";
      else if (!isValidPassword(formData.password)) nextErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.firstName.trim()) nextErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required";
    if (phoneError) nextErrors.phone = phoneError;
    if (!formData.address.trim()) nextErrors.address = "Street address is required";
    if (!formData.city.trim()) nextErrors.city = "Town / city is required";
    if (!formData.postalCode.trim()) nextErrors.postalCode = "Postcode is required";
    if (!formData.country) nextErrors.country = "Country is required";
    if (!formData.iban.trim()) nextErrors.iban = "IBAN is required";
    else if (!/^[A-Za-z0-9\s]{15,34}$/.test(formData.iban.trim())) nextErrors.iban = "Enter a valid IBAN";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    const nextValue = name === "phone" ? value.replace(/[^+0-9\s\-()]/g, "") : value;

    setFormData((current) => ({
      ...current,
      [name]: nextValue,
      ...(name === "country" ? { phoneCountry: nextValue } : {}),
    }));

    if (name === "email") {
      setErrors((current) => ({
        ...current,
        email: nextValue.trim() ? getEmailError(nextValue) : "",
      }));
      return;
    }

    if (name === "phone") {
      setErrors((current) => ({
        ...current,
        phone: nextValue.trim() ? getPhoneError(nextValue, selectedPhoneCountry?.isoCode, selectedPhoneCountry?.name) : "",
      }));
      return;
    }

    if (name === "phoneCountry" || name === "country") {
      const nextPhoneCountry = countries.find((country) => country.isoCode === nextValue);

      setErrors((current) => ({
        ...current,
        phone: formData.phone.trim()
          ? getPhoneError(formData.phone, nextPhoneCountry?.isoCode, nextPhoneCountry?.name)
          : current.phone,
      }));
    }

    if (errors[name as keyof CheckoutForm]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "email") {
      setErrors((current) => ({ ...current, email: getEmailError(value) }));
    }

    if (name === "phone") {
      setErrors((current) => ({ ...current, phone: getPhoneError(value, selectedPhoneCountry?.isoCode, selectedPhoneCountry?.name) }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) {
      showError("Please fix the highlighted checkout fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      const ibanResponse = await subscriptionService.validateIban({
        iban: formData.iban.trim(),
      });

      if (!ibanResponse.data.valid) {
        throw new Error(ibanResponse.data.message || "IBAN validation failed.");
      }

      if (!authStorage.hasToken()) {
        const signupResponse = await authService.signup({
          name: getFullName(formData.firstName, formData.lastName),
          email: formData.email.trim(),
          password: formData.password,
        });

        authStorage.setToken(signupResponse.data.token);
        authStorage.setUser(signupResponse.data.user);
        setIsLoggedIn(true);
      }

      const sessionResponse = await subscriptionService.createPaymentSession({
        plan_id: planId,
        plan_name: planApiName,
        amount: planAmount,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        country: formData.country,
        address: formData.address.trim(),
        city: formData.city.trim(),
        postal_code: formData.postalCode.trim(),
        phone: submitPhone,
      });

      const sessionToken = sessionResponse.data.sessionToken;

      const submitResponse = await subscriptionService.submitPayment({
        session_token: sessionToken,
        iban: formData.iban.trim(),
        iban_holder_name: getFullName(formData.firstName, formData.lastName),
      });

      const verifyResponse = await subscriptionService.verifyPayment({
        session_token: sessionToken,
        verification_id: extractVerificationId(submitResponse.data.verificationUrl),
      });

      if (verifyResponse.success) {
        authStorage.setSubscriptionActive(
          verifyResponse.data.subscriptionActive || verifyResponse.data.status === "COMPLETED"
        );
        setShowPaymentNotice(true);
        return;
      }

      throw new Error(verifyResponse.message || "Payment verification failed.");
    } catch (error) {
      showError(getErrorMessage(error, "Unable to complete payment."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentNoticeOk = () => {
    setShowPaymentNotice(false);
    navigate(PATHS.account, { replace: true });
  };

  return (
    <div>
      <Header />

      <section className="main">
        <div className="container checkout-page-wrap">
          <form className="checkout-panel-card" onSubmit={handleSubmit} noValidate>
            <div className="details-card">
              <h2 className="font-bold checkout-title">
                Complete Payment for {planDisplayName}
              </h2>
              <p className="checkout-note">
                Server-to-server checkout: validate your IBAN and payment will
                be submitted automatically.
              </p>
              <h2 className="font-bold checkout-title">Your Information</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full" style={isLoggedIn ? { gridColumn: "1 / -1" } : undefined}>
                    <label>
                      Email <span>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="john.doe@example.com"
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                    />
                    {errors.email && <p className="text-xs font-bold text-red-500 mt-1">{errors.email}</p>}
                  </div>
                  {!isLoggedIn ? (
                    <div className="form-group full">
                      <label>
                        Password <span>*</span>
                      </label>
                      <span className="password-input">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="show-password-input"
                          onClick={() => setShowPassword((current) => !current)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        />
                      </span>
                      {errors.password && <p className="text-xs font-bold text-red-500 mt-1">{errors.password}</p>}
                    </div>
                  ) : null}
                </div>
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      First name <span>*</span>
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" autoComplete="given-name" />
                    {errors.firstName && <p className="text-xs font-bold text-red-500 mt-1">{errors.firstName}</p>}
                  </div>
                  <div className="form-group full">
                    <label>
                      Last name <span>*</span>
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" autoComplete="family-name" />
                    {errors.lastName && <p className="text-xs font-bold text-red-500 mt-1">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="form-group full">
                  <label>
                    Phone <span>*</span>
                  </label>
                  <div className="phone-input-row">
                    <div className="phone-country-select" ref={phoneCountryRef}>
                      <button
                        type="button"
                        className="phone-country-trigger"
                        onClick={() => setShowPhoneCountryMenu((current) => !current)}
                        disabled={isDetectingCountry}
                        aria-haspopup="listbox"
                        aria-expanded={showPhoneCountryMenu}
                        aria-label={isDetectingCountry ? "Detecting phone country code" : "Phone country code"}
                      >
                        {selectedPhoneCountry ? (
                          <img src={getFlagImageUrl(selectedPhoneCountry.isoCode)} alt="" />
                        ) : null}
                        <span>{phoneCountryLabel}</span>
                      </button>
                      {showPhoneCountryMenu ? (
                        <div className="phone-country-menu">
                          <input
                            ref={phoneCountrySearchRef}
                            type="search"
                            className="phone-country-search"
                            value={phoneCountrySearch}
                            onChange={(event) => setPhoneCountrySearch(event.target.value)}
                            placeholder="Search country"
                            autoComplete="off"
                          />
                          <div role="listbox">
                            {filteredPhoneCountries.map((country) => {
                              const countryPhoneCode = formatPhoneCode(country.phonecode);

                              return (
                                <button
                                  type="button"
                                  key={country.isoCode}
                                  className="phone-country-option"
                                  onClick={() => handlePhoneCountrySelect(country.isoCode)}
                                  role="option"
                                  aria-selected={country.isoCode === formData.phoneCountry}
                                >
                                  <img src={getFlagImageUrl(country.isoCode)} alt="" />
                                  <span>{country.name}</span>
                                  <strong>{countryPhoneCode}</strong>
                                </button>
                              );
                            })}
                            {!filteredPhoneCountries.length ? (
                              <div className="phone-country-empty">No country found</div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="70 123 45 67"
                      autoComplete="tel-national"
                      inputMode="tel"
                      aria-invalid={Boolean(errors.phone)}
                    />
                  </div>
                  {errors.phone && <p className="text-xs font-bold text-red-500 mt-1">{errors.phone}</p>}
                </div>
              </div>
              <h2 className="font-bold checkout-title">Your Billing Details</h2>
              <div className="billing-details-fields">
                <div className="form-row">
                  <div className="form-group full">
                    <label>
                      Street address <span>*</span>
                    </label>
                    <input type="text" name="address" value={formData.address} onChange={handleChange} autoComplete="street-address" />
                    {errors.address && <p className="text-xs font-bold text-red-500 mt-1">{errors.address}</p>}
                  </div>
                  <div className="form-group full">
                    <label>
                      Town / City <span>*</span>
                    </label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} autoComplete="address-level2" />
                    {errors.city && <p className="text-xs font-bold text-red-500 mt-1">{errors.city}</p>}
                  </div>
                </div>
                <div className="form-row bottom-row">
                  <div className="form-group postcode">
                    <label>
                      Postcode / ZIP <span>*</span>
                    </label>
                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} autoComplete="postal-code" />
                    {errors.postalCode && <p className="text-xs font-bold text-red-500 mt-1">{errors.postalCode}</p>}
                  </div>
                  <div className="form-group country">
                    <label>
                      Country <span>*</span>
                    </label>
                    <div className="select-wrap">
                      <select
                        className="custom-select h-[46px]"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        autoComplete="country"
                        disabled={isDetectingCountry}
                      >
                        {countries.map((country) => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.country && <p className="text-xs font-bold text-red-500 mt-1">{errors.country}</p>}
                  </div>
                </div>
              </div>
              <h2 className="font-bold checkout-title">
                SEPA Direct Debit Information
              </h2>
              <div className="payment-box">
                <label className="payment-header">
                  <input type="radio" name="payment" id="sepaRadio" checked readOnly />
                  <div className="custom-radio" />
                  <span className="payment-title">SEPA Direct Debit</span>
                  <img src=".\sepa.svg" alt="SEPA" />
                </label>
                <div className="payment-content">
                  <p className="checkout-note">
                    Mandate Information.
                    <br /> By providing your IBAN and confirming this payment,
                    you are authorizing us to send instructions to your bank to
                    debit your account and your bank to debit your account in
                    accordance with those instructions. You are entitled to a
                    refund from your bank under the terms and conditions of your
                    agreement with your bank. A refund must be claimed within 8
                    weeks starting from the date on which your account was
                    debited.
                  </p>
                  <div className="checkout-fields">
                    <label>
                      IBAN <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="iban"
                      value={formData.iban}
                      onChange={handleChange}
                      placeholder="IBAN (e.g. DE89370400440532013000)"
                      autoComplete="off"
                      className="checkout-input"
                    />
                    {errors.iban && <p className="text-xs font-bold text-red-500 mt-1">{errors.iban}</p>}
                  </div>
                  <h2 className="font-bold checkout-title">
                    Payment Information
                  </h2>
                  <p className="mb-0 checkout-note">
                    All transactions are secure and encrypted. Credit card
                    information is never stored on our servers.
                  </p>
                </div>
              </div>
              <p className="mt-3 text-[#4b5563] text-center font-medium">
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our privacy policy .
              </p>
              <div className="place-btn-wrapper flex justify-center">
                <button
                  type="submit"
                  className="font-semibold place-order w-full sm:w-auto bg-[#2e3543] text-white px-10 py-4 rounded-2xl font-bold hover:bg-slate-800 transition-all hover:-translate-y-1 text-sm tracking-wide shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center min-h-[56px]"
                  name="place_order"
                  id="place_order"
                  value="Complete Your Order Now"
                  data-value="Complete Your Order Now"
                  disabled={isSubmitting}
                  aria-label={isSubmitting ? "Processing order" : "Complete Your Order Now"}
                >
                  {isSubmitting ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    "Complete Your Order Now"
                  )}
                </button>
              </div>
              <div className="sepa-icon">
                <img
                  src=".\SEPA Logo PNG Vector (SVG) Free Download.jpg"
                  alt="SEPA"
                />
              </div>
              <div className="checkout-features">
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\time.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Cancel Anytime</strong>
                    Cancel anytime, see T&amp;Cs for more info
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-title">
                    <img src=".\lock.svg" alt="" />
                  </div>
                  <div className="feature-text text-[#4b5563]">
                    <strong>Secure Processing</strong>
                    Encrypted, secure order processing
                  </div>
                </div>
              </div>
              <p className="text-center checkout-note">
                We use GoCardless to process your Direct Debit payments. More
                information on how GoCardless processes your personal data and
                your data protection rights, including your right to object, is
                available at{" "}
                <a href="https://gocardless.com/legal/privacy/" target="_blank" rel="noopener noreferrer">
                  gocardless.com/legal/privacy/
                </a>
              </p>
              <div className="guarantee-box">
                <div className="logo-wrap">
                  <img src=".\direct debit.png" alt="Direct Debit Logo" />
                </div>
                <div className="guarantee-title">Direct Debit Guarantee</div>
              </div>
            </div>
            <div className="overflow-hidden subscription-card">
              <div className="absolute bottom-[-300px] right-0 w-[250px] h-[250px] md:w-[400px] md:h-[400px] bg-[#cbd5e1] blur-[80px] md:blur-[100px] rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none opacity-30 md:opacity-100"></div>
              <h2>Subscription Information</h2>
              <div className="subscription-item">
                <div className="product-image">
                  <img src="/favicon.ico" alt="LearnSocialAI" />
                </div>
                <div className="product-info">
                  <div className="top-row">
                    <h3>{planApiName}</h3>
                    <span className="price">{formattedAmount}</span>
                  </div>
                  <p>Rebills every month</p>
                  <div className="font-light billing-info text-[#94a3b8] text-[14px]">
                    <span>Next Billing:</span>
                    <span>{nextBillingDate}</span>
                  </div>
                </div>
              </div>

              <div className="divider" />
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formattedAmount}</span>
              </div>
              <div className="divider" />
              <div className="total-row">
                <span>Total</span>
                <span>{formattedAmount}</span>
              </div>
            </div>
          </form>
        </div>
      </section>

      {showPaymentNotice ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4" role="dialog" aria-modal="true" aria-labelledby="payment-notice-title">
          <div className="payment-notice-modal">
            <h2 id="payment-notice-title" className="text-3xl font-bold text-[#2e3543] mb-4">
              Payment Submitted
            </h2>
            <p className="text-[#4b5563] font-medium leading-7 mb-4">
              Your payment request has been received. The amount will be deducted from your IBAN within 2-3 business days.
            </p>
            <p className="text-[#2e3543] font-bold mb-8">
              Redirecting you in {redirectSeconds}
            </p>
            <button
              type="button"
              onClick={handlePaymentNoticeOk}
              className="bg-[#2e3543] text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
};
